import React, { useEffect, useState } from "react";
import styles from "./BlogInfo.module.css";
import { useParams, useNavigate } from "react-router-dom";
import { open_api } from "../../Utils/network";
import { Helmet } from "react-helmet";
import ArticleHeader from "./ArticleHeader";
import AppPromotion from "../Card/AppPromotion";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
} from "react-share";

function BlogInfo({ data, relatedBlogs }) {
  const [blogReviews, setBlogReviews] = useState();
  const [linkCopied, setLinkCopied] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const article = document.querySelector('[data-article-body]');
      if (!article) return;
      const top = article.offsetTop;
      const height = article.offsetHeight;
      const scrolled = Math.max(0, Math.min(1,
        (window.scrollY - top + window.innerHeight * 0.3) / height
      ));
      setReadProgress(Math.round(scrolled * 100));
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = data?.title || "";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (slug) {
      getBlogComments(slug);
    }
  }, [slug]);

  const getBlogComments = (slug) => {
    open_api
      .get(`blog/pages/${slug}/comments/`)
      .then((res) => {
        setBlogReviews(res?.data?.data);
      })
      .catch(() => {});
  };

  const convertToEmbedUrl = (url) => {
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = url.includes("youtube.com")
        ? url.split("v=")[1]
        : url.split("/").pop();
      const ampersandPosition = videoId?.indexOf("&");
      return ampersandPosition !== -1
        ? `https://www.youtube.com/embed/${videoId.substring(0, ampersandPosition)}`
        : `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  const renderCustomEmbed = (block) => {
    if (!block.value || !block.value.embed_code) return null;
    return (
      <div
        key={block.id}
        className={styles.customEmbed}
        dangerouslySetInnerHTML={{ __html: block.value.embed_code }}
      />
    );
  };

  const loadExternalScript = (src) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }
  };

  useEffect(() => {
    loadExternalScript("https://www.instagram.com/embed.js");
    loadExternalScript("https://platform.twitter.com/widgets.js");
  }, []);

  const handleRelatedNav = (item) => {
    navigate(`/blogs/${item?.meta?.slug || item?.id}`);
  };

  return (
    <>
      <Helmet>
        <title>{data?.meta?.seo_title || data?.title}</title>
        <meta
          name="description"
          content={data?.meta?.search_description || data?.intro}
        />
      </Helmet>

      {/* Reading Progress Bar */}
      <div
        className={styles.progress_bar}
        style={{ width: `${readProgress}%` }}
        aria-hidden="true"
      />

      <article className={styles.article}>
        <ArticleHeader data={data} />

        {/* Article Body + Share Sidebar */}
        <div className={styles.article_layout}>
          <aside className={styles.share_sidebar}>
            <div className={styles.share_sidebar_inner}>
              <WhatsappShareButton url={shareUrl} title={shareTitle}>
                <WhatsappIcon size={36} round />
              </WhatsappShareButton>
              <FacebookShareButton url={shareUrl} quote={shareTitle}>
                <FacebookIcon size={36} round />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={shareTitle}>
                <TwitterIcon size={36} round />
              </TwitterShareButton>
              <LinkedinShareButton url={shareUrl} title={shareTitle}>
                <LinkedinIcon size={36} round />
              </LinkedinShareButton>
              <button
                className={`${styles.share_copy_btn} ${linkCopied ? styles.share_copy_btn_copied : ""}`}
                onClick={handleCopyLink}
                aria-label="Copy link"
                title={linkCopied ? "Copied!" : "Copy link"}
              >
                {linkCopied ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                )}
              </button>
            </div>
          </aside>

          <div className={styles.article_body} data-article-body>
          {data?.intro && (
            <p className={styles.lede}>{data.intro}</p>
          )}

          {data?.body?.map((block) => {
            switch (block.type) {
              case "h1":
                return <h1 key={block.id}>{block.value}</h1>;
              case "h2":
                return <h2 key={block.id}>{block.value}</h2>;
              case "paragraph":
                return (
                  <p
                    key={block.id}
                    dangerouslySetInnerHTML={{ __html: block.value }}
                  />
                );
              case "image_carousel":
                return (
                  <div key={block.id} className={styles.imageCarousel}>
                    {block.value.map((image, idx) => (
                      <img
                        key={idx}
                        src={image.url}
                        alt={`Image ${idx + 1}`}
                        className={styles.carouselImage}
                      />
                    ))}
                  </div>
                );
              case "thumbnail_gallery":
                return (
                  <div key={block.id} className={styles.thumbnailGallery}>
                    {block.value.map((image, idx) => (
                      <img
                        key={idx}
                        src={image.url}
                        alt={`Thumbnail ${idx + 1}`}
                        className={styles.thumbnailImage}
                      />
                    ))}
                  </div>
                );
              case "image_text":
                return (
                  <div key={block.id} className={styles.imageTextBlock}>
                    <img
                      src={block.value.image.url}
                      alt="Text with Image"
                      className={styles.imageTextImage}
                    />
                    <div
                      dangerouslySetInnerHTML={{ __html: block.value.text }}
                      className={styles.imageTextContent}
                    />
                  </div>
                );
              case "video":
                return (
                  <div key={block.id} className={styles.videoContainer}>
                    <iframe
                      src={convertToEmbedUrl(block.value)}
                      title="Video Player"
                      width="560"
                      height="315"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className={styles.videoIframe}
                    ></iframe>
                  </div>
                );
              case "custom_embed":
                return renderCustomEmbed(block);
              default:
                return null;
            }
          })}

          </div>
        </div>

        {/* App Download CTA */}
        <div className={styles.promo_wrapper}>
          <AppPromotion />
        </div>
      </article>

      {/* More from the Journal */}
      {relatedBlogs && relatedBlogs.length > 0 && (
        <section className={styles.more}>
          <div className={styles.more_inner}>
            <div className={styles.more_header}>
              <h3 className={styles.more_title}>More from the journal</h3>
              <a href="/blogs" className={styles.more_all}>
                All posts →
              </a>
            </div>
            <div className={styles.more_grid}>
              {relatedBlogs.slice(0, 2).map((item, index) => (
                <div
                  key={item.id || index}
                  className={styles.more_card}
                  onClick={() => handleRelatedNav(item)}
                >
                  <div className={styles.more_card_img}>
                    <img
                      src={item?.header_image_url?.url}
                      alt={item?.title}
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.more_card_body}>
                    {item?.tags?.[0] && (
                      <span className={styles.more_card_cat}>
                        {typeof item.tags[0] === "string"
                          ? item.tags[0]
                          : item.tags[0].name}
                      </span>
                    )}
                    {item?.read_time && (
                      <span className={styles.more_card_time}>
                        {item.read_time} min read
                      </span>
                    )}
                    <h4 className={styles.more_card_title}>{item?.title}</h4>
                    <p className={styles.more_card_desc}>
                      {item?.intro?.length > 120
                        ? `${item.intro.substring(0, 120)}...`
                        : item?.intro}
                    </p>
                    <span className={styles.more_card_link}>Read</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export default BlogInfo;
